import { useEffect, useState } from 'react';
import { colors, fieldLabelStyle, inputStyle, lavenderButtonStyle, primaryButtonStyle } from '../lib/theme';
import type { DishProductInput } from '../types';

interface Props {
  productName: string;
  caloriesPer100g: number;
  value: DishProductInput;
  onSave: (value: DishProductInput) => void;
  onRemove: () => void;
  onClose: () => void;
}

function computeCalories(caloriesPer100g: number, grams: number): number {
  return Math.round((caloriesPer100g * grams) / 100);
}

export default function IngredientAmountModal({
  productName,
  caloriesPer100g,
  value,
  onSave,
  onRemove,
  onClose,
}: Props) {
  const [grams, setGrams] = useState(value.amountGrams != null ? String(value.amountGrams) : '');
  const [kcal, setKcal] = useState(value.calories != null ? String(value.calories) : '');
  const [kcalTouched, setKcalTouched] = useState(false);

  useEffect(() => {
    setGrams(value.amountGrams != null ? String(value.amountGrams) : '');
    setKcal(value.calories != null ? String(value.calories) : '');
    setKcalTouched(false);
  }, [value]);

  function handleGramsChange(next: string) {
    setGrams(next);
    if (!kcalTouched) {
      const g = Number(next);
      setKcal(next && !Number.isNaN(g) ? String(computeCalories(caloriesPer100g, g)) : '');
    }
  }

  function handleSave() {
    onSave({
      productId: value.productId,
      amountGrams: grams === '' ? null : Number(grams),
      calories: kcal === '' ? null : Number(kcal),
    });
    onClose();
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(26, 24, 21, 0.35)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 60,
        padding: '1rem',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: colors.bone,
          borderRadius: 24,
          padding: '1.5rem',
          width: '100%',
          maxWidth: 320,
          boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
        }}
      >
        <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>{productName}</div>
        <div style={{ fontSize: '0.8rem', opacity: 0.65, marginBottom: '0.9rem' }}>
          {caloriesPer100g} kcal / 100g
        </div>

        <label style={{ ...fieldLabelStyle, marginBottom: '0.75rem' }}>
          Amount (g)
          <input
            type="number"
            min={0}
            autoFocus
            value={grams}
            onChange={(e) => handleGramsChange(e.target.value)}
            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', padding: '0.6rem 0.85rem' }}
          />
        </label>

        <label style={{ ...fieldLabelStyle, marginBottom: '1.25rem' }}>
          Calories (kcal)
          <input
            type="number"
            min={0}
            value={kcal}
            onChange={(e) => {
              setKcal(e.target.value);
              setKcalTouched(true);
            }}
            style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', padding: '0.6rem 0.85rem' }}
          />
        </label>

        <div style={{ display: 'flex', justifyContent: 'space-between', gap: '0.5rem' }}>
          <button
            type="button"
            onClick={() => {
              onRemove();
              onClose();
            }}
            style={{
              background: 'none',
              border: 'none',
              color: colors.redText,
              fontWeight: 600,
              cursor: 'pointer',
              padding: '0.5rem 0',
            }}
          >
            Remove
          </button>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button
              type="button"
              onClick={onClose}
              style={lavenderButtonStyle}
            >
              Cancel
            </button>
            <button type="button" onClick={handleSave} style={primaryButtonStyle}>
              Save
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
